const hasUnsafePathCharacter = (value) => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x20 || code === 0x7f || code === 0x5c) return true;
  }
  return false;
};

// Navigation state only: never copy credentials, reset tokens or email into URLs.
export function safeReturnPath(value, fallback = null) {
  if (typeof value !== 'string' || !value.startsWith('/')) return fallback;
  // Check both the raw and encoded forms. Browsers normalize backslashes as
  // slashes in special URLs; neither protocol-relative nor control-char paths
  // are a valid post-auth destination.
  let decoded = value;
  try {
    for (let pass = 0; pass < 8; pass += 1) {
      if (!decoded.startsWith('/') || decoded.startsWith('//') || hasUnsafePathCharacter(decoded)) return fallback;
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      if (pass === 7) return fallback;
      decoded = next;
    }
    if (decoded.startsWith('//') || hasUnsafePathCharacter(decoded)) return fallback;
    const parsed = new URL(value, 'https://henosis.invalid');
    if (parsed.origin !== 'https://henosis.invalid' || parsed.pathname.startsWith('//')) return fallback;
    // Use the decoded, canonical path too, so encoded names and dot segments
    // cannot send an authenticated user straight back into an auth screen.
    const decodedUrl = new URL(decoded, 'https://henosis.invalid');
    if (decodedUrl.pathname.startsWith('//')) return fallback;
    if (/^\/(login|register|reset-password)(\/|$)/i.test(decodedUrl.pathname)) return fallback;
    // Reject rather than echo credential-bearing destinations. Both query and
    // fragment parameters can otherwise leak through the nested returnTo URL.
    if (/[?&#](?:email|(?:(?:new|old|current|confirm)[_-]?)?password|passwd|(?:(?:access|refresh|reset|id|session)[_-]?)?token|api[_-]?key|(?:client[_-]?)?secret|(?:verification[_-]?)?code|credentials|authorization|draft)=/i.test(decoded)) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_) {
    return fallback;
  }
}

export function authReturnTo(location, fallback = '/chat') {
  const params = new URLSearchParams(location?.search || '');
  return safeReturnPath(params.get('returnTo'))
    || safeReturnPath(location?.state?.returnUrl)
    || safeReturnPath(fallback);
}

export function authRoute(path, location, includeDefault = false, { enabled = process.env.REACT_APP_EXPERIENCE_ONBOARDING_ENABLED === 'true' } = {}) {
  const intent = new URLSearchParams(location?.search || '').get('intent');
  // Preserve the old auth-link behavior until rollout is enabled. New
  // post-login destination helpers never interpret intent as an explicit return.
  const destination = enabled !== true && intent === 'activate'
    ? '/activate'
    : authReturnTo(location, includeDefault ? '/chat' : null);
  const params = new URLSearchParams();
  if (destination) params.set('returnTo', destination);
  // Pricing intent is metadata, not authorization to start checkout. Carry
  // only known intent; never copy arbitrary auth query/state fields.
  if (enabled === true && intent === 'activate') {
    params.set('intent', 'activate');
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

// For a protected route redirect, use the actual requested resource rather
// than a returnTo parameter supplied on that resource. Do not use this helper
// when merely switching between login and registration (use authRoute).
export function authRedirectRoute(path, location) {
  const destination = safeReturnPath(`${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`);
  const intent = new URLSearchParams(location?.search || '').get('intent');
  return authRoute(path, { search: intent === 'activate' ? '?intent=activate' : '', state: { returnUrl: destination } }, false, { enabled: true });
}

export function authErrorMessage(data, fallback) {
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (Array.isArray(data?.detail)) {
    const messages = data.detail.map(item => typeof item?.msg === 'string' ? item.msg : '').filter(Boolean);
    if (messages.length) return messages.join('; ');
  }
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  if (data?.error && typeof data.error === 'object') {
    const messages = Object.entries(data.error).map(([field, value]) => {
      const text = Array.isArray(value) ? value.join(', ') : String(value ?? '');
      return text ? `${field}: ${text}` : '';
    }).filter(Boolean);
    if (messages.length) return messages.join('; ');
  }
  return fallback;
}
